import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useSettings } from '../../context/SettingsContext';
import { money } from '../../utils/format';
import { IconClose, IconTrash, IconPlus, IconMinus } from '../common/Icons';
import Image from '../common/Image';

export default function CartDrawer() {
  const { items, open, setOpen, update, remove, subtotal } = useCart();
  const { settings } = useSettings();
  const symbol = settings.shop?.currencySymbol || '$';
  const freeOver = Number(settings.shop?.freeShippingOver || 0);
  const remaining = freeOver ? Math.max(0, freeOver - subtotal) : 0;

  return (
    <div className={`eg-drawer-backdrop ${open ? 'is-open' : ''}`} onClick={() => setOpen(false)}>
      <aside className={`eg-cart-drawer ${open ? 'is-open' : ''}`} onClick={(e) => e.stopPropagation()} aria-label="Shopping cart">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="text-lg font-medium">Shopping Cart ({items.reduce((s, i) => s + i.quantity, 0)})</h3>
          <button type="button" onClick={() => setOpen(false)} aria-label="Close cart" className="p-1"><IconClose className="w-5 h-5" /></button>
        </div>
        {freeOver > 0 && (
          <div className="px-5 py-3 text-sm bg-gray-50 border-b">
            {remaining > 0 ? (
              <>Spend <strong>{money(remaining, symbol)}</strong> more for free shipping</>
            ) : (
              <span className="text-green-700">🎉 You’ve unlocked free shipping!</span>
            )}
            <div className="eg-progress mt-2"><span style={{ width: `${Math.min(100, (subtotal / freeOver) * 100)}%` }} /></div>
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-5">
          {items.length === 0 ? (
            <div className="py-16 text-center text-color-secondary">
              <p className="mb-6">Your cart is currently empty.</p>
              <Link to="/collections/all" className="sf__btn sf__btn-primary" onClick={() => setOpen(false)}>Continue shopping</Link>
            </div>
          ) : (
            items.map((it) => (
              <div key={`${it.productId}:${it.variantId}`} className="flex py-4 border-b">
                <Link to={`/products/${it.slug}`} className="w-20 flex-shrink-0" onClick={() => setOpen(false)}>
                  <Image src={it.image} alt={it.title} ratio={1} />
                </Link>
                <div className="flex-1 pl-4">
                  <Link to={`/products/${it.slug}`} className="font-medium leading-snug block" onClick={() => setOpen(false)}>{it.title}</Link>
                  {it.variantTitle && <p className="text-sm text-color-secondary">{it.variantTitle}</p>}
                  <div className="flex items-center justify-between mt-2">
                    <div className="eg-qty">
                      <button type="button" onClick={() => update(it.productId, it.variantId, it.quantity - 1)} aria-label="Decrease"><IconMinus className="w-3 h-3" /></button>
                      <input value={it.quantity} onChange={(e) => update(it.productId, it.variantId, Math.max(1, parseInt(e.target.value, 10) || 1))} aria-label="Quantity" />
                      <button type="button" onClick={() => update(it.productId, it.variantId, it.quantity + 1)} aria-label="Increase"><IconPlus className="w-3 h-3" /></button>
                    </div>
                    <span className="font-medium">{money(it.price * it.quantity, symbol)}</span>
                  </div>
                </div>
                <button type="button" className="ml-2 self-start p-1 text-color-secondary hover:text-black" onClick={() => remove(it.productId, it.variantId)} aria-label="Remove"><IconTrash className="w-4 h-4" /></button>
              </div>
            ))
          )}
        </div>
        {items.length > 0 && (
          <div className="px-5 py-4 border-t">
            <div className="flex justify-between text-lg font-medium mb-1"><span>Subtotal</span><span>{money(subtotal, symbol)}</span></div>
            <p className="text-sm text-color-secondary mb-4">Shipping & taxes calculated at checkout</p>
            <Link to="/cart" className="sf__btn sf__btn-secondary w-full block text-center mb-2" onClick={() => setOpen(false)}>View cart</Link>
            <Link to="/checkout" className="sf__btn sf__btn-primary w-full block text-center" onClick={() => setOpen(false)}>Check out</Link>
          </div>
        )}
      </aside>
    </div>
  );
}
